/* ====================================================================
   BUENOFE System Builder — bt-builder.js
   ES module. No window globals. All custom elements are "bt-" prefixed.
   Cart integration follows the Impact theme contract exactly:
     cart:prepare-bundled-sections -> POST /cart/add.js (items[]) ->
     GET /cart.js -> dispatch variant:add + cart:change
   The theme cart drawer opens & re-renders itself; we never touch its DOM.
   ==================================================================== */

const STORAGE_KEY = 'btb:setup:v1';

/* ---------------- money ---------------- */
function moneyFormat() {
  return (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat)
    || document.querySelector('bt-builder-app')?.dataset.moneyFormat
    || '${{amount}}';
}
function formatMoney(cents, format) {
  format = format || moneyFormat();
  const value = (m) => {
    switch (m) {
      case 'amount': return (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      case 'amount_no_decimals': return Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      case 'amount_with_comma_separator': return (cents / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      case 'amount_no_decimals_with_comma_separator': return Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      case 'amount_with_apostrophe_separator': return (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
      default: return (cents / 100).toFixed(2);
    }
  };
  // Merchant currency formats may embed HTML (e.g. "<span class=money>${{amount}}</span>").
  // All builder call sites are text contexts (textContent / clipboard), so strip tags.
  return stripHtml(format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, m) => value(m)));
}
function stripHtml(str) {
  return String(str).replace(/<[^>]*>/g, '').trim();
}

/* ---------------- registry (variant metadata rendered by Liquid) ---------------- */
const registry = new Map(); // vid(string) -> {pid,t,o,p,pf,img,url,av}
function ingestRegistries(root) {
  (root || document).querySelectorAll('script[data-btb-registry]').forEach((s) => {
    if (s.dataset.btbIngested) return;
    try {
      const data = JSON.parse(s.textContent);
      Object.entries(data).forEach(([vid, meta]) => {
        if (vid !== '__sentinel__' && meta && typeof meta === 'object') registry.set(String(vid), meta);
      });
      s.dataset.btbIngested = '1';
    } catch (e) { console.warn('[bt-builder] bad registry JSON', e); }
  });
}

/* ---------------- root route ---------------- */
function rootRoute() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

/* ==================================================================== */
class BtBuilderApp extends HTMLElement {
  constructor() {
    super();
    this.state = new Map(); // vid(string) -> qty (My Setup — draft list)
    this.cartPids = new Set(); // product ids currently in the Shopify cart
    this._abort = new AbortController();
  }

  connectedCallback() {
    ingestRegistries();
    this._restore();
    this._buildTabs();
    this._bind();
    this._renderPanel();
    this._markInKit();
    this._initCartSync();
  }

  disconnectedCallback() { this._abort.abort(); }

  /* ---------------- tabs / stages ---------------- */
  get stages() { return Array.from(document.querySelectorAll('[data-btb-stage]')); }

  // one tab per group: stages sharing a tab label show together, a blank label is its own tab
  get groups() {
    const groups = [];
    const byLabel = new Map();
    this.stages.forEach((stage) => {
      const label = stage.dataset.tab;
      if (label && byLabel.has(label)) { byLabel.get(label).stages.push(stage); return; }
      const group = { name: label || stage.dataset.name, stages: [stage] };
      if (label) byLabel.set(label, group);
      groups.push(group);
    });
    return groups;
  }

  _buildTabs() {
    const groups = this.groups;
    if (groups.length < 2) return;
    const anchor = groups[0].stages[0].querySelector('[data-btb-tabs-anchor]');
    if (!anchor || anchor.querySelector('.btb-tabs')) return;

    const nav = document.createElement('nav');
    nav.className = 'btb-tabs';
    nav.setAttribute('role', 'tablist');
    groups.forEach((group, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btb-tab';
      btn.setAttribute('role', 'tab');
      btn.dataset.btbTab = String(i);
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      btn.innerHTML = `<span class="btb-tab__no">0${i + 1}</span>${group.name || `System ${i + 1}`}`;
      nav.appendChild(btn);
    });
    anchor.appendChild(nav);
    this._activateStage(0);
  }

  _activateStage(index) {
    const groups = this.groups;
    groups.forEach((group, i) => group.stages.forEach((stage) => {
      const root = stage.closest('.shopify-section') || stage;
      if (i === index) root.removeAttribute('data-btb-hidden');
      else root.setAttribute('data-btb-hidden', '');
    }));
    // keep the tab row visible: move it into the active group's first stage anchor
    const nav = document.querySelector('.btb-tabs');
    const target = groups[index] && groups[index].stages[0].querySelector('[data-btb-tabs-anchor]');
    if (nav && target && nav.parentElement !== target) target.appendChild(nav);
    document.querySelectorAll('.btb-tab').forEach((t, i) =>
      t.setAttribute('aria-selected', i === index ? 'true' : 'false'));
  }

  /* ---------------- event wiring (single delegated listener) ---------------- */
  _bind() {
    const { signal } = this._abort;

    document.addEventListener('click', (e) => {
      const tab = e.target.closest('[data-btb-tab]');
      if (tab) { this._activateStage(Number(tab.dataset.btbTab)); return; }

      const add = e.target.closest('[data-btb-add]');
      if (add && !add.disabled) {
        let vid = add.dataset.btbAdd;
        if (vid === 'select') {
          const sel = add.closest('[data-btb-scope]')?.querySelector('select[data-btb-select]');
          vid = sel && sel.value;
        }
        if (vid) {
          this.add(vid, 1);
          const pop = add.closest('x-popover');
          if (pop && typeof pop.hide === 'function') pop.hide();
        }
        return;
      }

      const bundle = e.target.closest('[data-btb-bundle]');
      if (bundle) {
        try {
          const items = JSON.parse(bundle.dataset.btbBundle);
          items.forEach(([vid, qty]) => this.add(String(vid), qty || 1, true));
          this._toast(`${bundle.dataset.btbBundleName || 'Combo'} added — ${items.length} ${items.length === 1 ? 'piece' : 'pieces'}`);
        } catch (err) { console.warn('[bt-builder] bad bundle payload', err); }
        return;
      }

      const qty = e.target.closest('[data-btb-qty]');
      if (qty) { this._changeQty(qty.dataset.btbVid, Number(qty.dataset.btbQty)); return; }

      if (e.target.closest('[data-btb-clear]')) { this.clear(); return; }
      if (e.target.closest('[data-btb-toggle]')) { this._togglePanel(); return; }
      if (e.target.closest('[data-btb-checkout]')) { this.addSetupToCart(); return; }
      if (e.target.closest('[data-btb-copy]')) { this.copyList(); return; }
    }, { signal });

    // popover price follows the option select
    document.addEventListener('change', (e) => {
      const sel = e.target.closest('select[data-btb-select]');
      if (!sel) return;
      const opt = sel.selectedOptions[0];
      const scope = sel.closest('[data-btb-scope]');
      const priceEl = scope && scope.querySelector('[data-btb-price]');
      if (priceEl && opt) priceEl.textContent = stripHtml(opt.dataset.pf || '');
      const addBtn = scope && scope.querySelector('[data-btb-add]');
      if (addBtn && opt) {
        const available = opt.dataset.av !== 'false';
        addBtn.disabled = !available;
        addBtn.textContent = available ? (addBtn.dataset.labelAdd || 'Add to setup') : (addBtn.dataset.labelSoldout || 'Sold out');
      }
    }, { signal });

    // Shopify theme editor support
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        const idx = this.groups.findIndex((g) => g.stages.some((s) => e.target.contains(s)));
        if (idx >= 0) this._activateStage(idx);
      }, { signal });
      document.addEventListener('shopify:section:load', () => {
        ingestRegistries();
        this._buildTabs();
        this._renderPanel();
        this._markInKit();
      }, { signal });
    }
  }

  /* ---------------- state ---------------- */
  add(vid, qty, quiet) {
    vid = String(vid);
    const meta = registry.get(vid);
    if (!meta || meta.av === false) return;
    this.state.set(vid, (this.state.get(vid) || 0) + (qty || 1));
    this._persist();
    this._renderPanel();
    this._markInKit();
    if (!quiet) this._toast(`${meta.t}${meta.o ? ' · ' + meta.o : ''} added`);
  }

  _changeQty(vid, delta) {
    vid = String(vid);
    const next = (this.state.get(vid) || 0) + delta;
    if (next <= 0) this.state.delete(vid); else this.state.set(vid, next);
    this._persist();
    this._renderPanel();
    this._markInKit();
  }

  clear() {
    this.state.clear();
    this._persist();
    this._renderPanel();
    this._markInKit();
  }

  _persist() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...this.state.entries()])); } catch (e) { /* private mode */ }
  }
  _restore() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) JSON.parse(raw).forEach(([vid, qty]) => {
        if (registry.has(String(vid))) this.state.set(String(vid), qty);
      });
    } catch (e) { /* ignore */ }
  }

  /* ---------------- rendering ---------------- */
  get subtotal() {
    let total = 0;
    this.state.forEach((qty, vid) => { const m = registry.get(vid); if (m) total += m.p * qty; });
    return total;
  }
  get count() {
    let n = 0; this.state.forEach((q) => { n += q; }); return n;
  }

  _renderPanel() {
    const itemsWrap = this.querySelector('[data-btb-items]');
    const emptyText = this.dataset.emptyText || 'Nothing here yet.';
    if (itemsWrap) {
      if (!this.state.size) {
        itemsWrap.innerHTML = `<div class="btb-panel__empty">${emptyText}</div>`;
      } else {
        itemsWrap.innerHTML = `<div class="btb-items">` + [...this.state.entries()].map(([vid, qty]) => {
          const m = registry.get(vid); if (!m) return '';
          return `<div class="btb-it">
            ${m.img ? `<img class="btb-it__img" src="${m.img}" alt="" loading="lazy">` : `<span class="btb-it__img"></span>`}
            <div class="btb-it__t"><b>${m.t}</b><span>${m.o || ' '}</span><span class="btb-it__lp">${formatMoney(m.p * qty)}</span></div>
            <div class="btb-qty">
              <button type="button" data-btb-qty="-1" data-btb-vid="${vid}" aria-label="Decrease quantity">−</button>
              <span class="btb-qty__q">${qty}</span>
              <button type="button" data-btb-qty="1" data-btb-vid="${vid}" aria-label="Increase quantity">+</button>
            </div>
          </div>`;
        }).join('') + `</div>`;
      }
    }
    const count = this.count, subtotal = this.subtotal;
    this.querySelectorAll('[data-btb-count]').forEach((el) => { el.textContent = count; });
    this.querySelectorAll('[data-btb-subtotal]').forEach((el) => { el.textContent = formatMoney(subtotal); });
    const cta = this.querySelector('[data-btb-checkout]');
    if (cta) cta.disabled = !count;
  }

  _markInKit() {
    const pids = new Set();
    this.state.forEach((_, vid) => { const m = registry.get(vid); if (m) pids.add(String(m.pid)); });
    document.querySelectorAll('.btb-dot[data-btb-pid]').forEach((dot) => {
      dot.classList.toggle('btb-dot--inkit', pids.has(dot.dataset.btbPid));
    });
  }

  /* ---------------- cart state on markers (distinct from My Setup) ----------------
     Ring (::before) = in My Setup (draft). Dark check badge = in the Shopify CART,
     kept in sync at load + on every cart:change / cart:refresh — so products added
     from PDPs show up too. */
  _initCartSync() {
    const { signal } = this._abort;
    fetch(`${rootRoute()}cart.js`).then((r) => r.json()).then((c) => this._syncCart(c)).catch(() => {});
    document.addEventListener('cart:change', (e) => { if (e.detail?.cart) this._syncCart(e.detail.cart); }, { signal });
    document.addEventListener('cart:refresh', () => {
      fetch(`${rootRoute()}cart.js`).then((r) => r.json()).then((c) => this._syncCart(c)).catch(() => {});
    }, { signal });
  }

  _syncCart(cart) {
    this.cartPids = new Set((cart.items || []).map((i) => String(i.product_id)));
    this._markCart();
  }

  _markCart() {
    document.querySelectorAll('.btb-dot[data-btb-pid]').forEach((dot) => {
      const incart = this.cartPids.has(dot.dataset.btbPid);
      if (incart && !dot.querySelector('.btb-dot__cart')) {
        dot.insertAdjacentHTML('beforeend', '<span class="btb-dot__cart" aria-hidden="true">&#10003;</span>');
      }
      dot.classList.toggle('btb-dot--incart', incart);
    });
    document.querySelectorAll('[data-btb-stage]').forEach((stage) => {
      const has = !!stage.querySelector('.btb-dot--incart');
      stage.querySelectorAll('[data-btb-incart-legend]').forEach((el) => { el.hidden = !has; });
    });
  }

  _togglePanel(force) {
    const panel = this.querySelector('.btb-panel');
    if (!panel) return;
    const open = typeof force === 'boolean' ? force : !panel.hasAttribute('open');
    panel.toggleAttribute('open', open);
    const fab = this.querySelector('.btb-fab');
    if (fab) fab.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /* ---------------- cart (theme contract) ---------------- */
  async addSetupToCart() {
    if (!this.state.size) return;
    const cta = this.querySelector('[data-btb-checkout]');
    const errEl = this.querySelector('[data-btb-error]');
    if (errEl) { errEl.removeAttribute('data-show'); errEl.textContent = ''; }
    if (cta) cta.setAttribute('aria-busy', 'true');

    const items = [...this.state.entries()].map(([vid, qty]) => ({ id: Number(vid), quantity: qty }));

    // 1. let theme components register their sections.
    //    Seed with 'variant-added' exactly like the theme's ProductForm — the
    //    cart-notification popover (cartType "popover") renders from that section.
    const sectionsToBundle = ['variant-added'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true, detail: { sections: sectionsToBundle }
    }));

    try {
      // 2. single items[] call — one cart:change, one drawer morph, one opening
      const response = await fetch(`${rootRoute()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: sectionsToBundle.join(','), sections_url: `${rootRoute()}variants/${items[0].id}` })
      });
      const responseJson = await response.json();

      if (!response.ok) {
        this.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: responseJson.description || responseJson.message || 'Could not add to cart' } }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        if (errEl) { errEl.textContent = responseJson.description || 'Some items could not be added (sold out?). Please review your setup.'; errEl.setAttribute('data-show', ''); }
        return;
      }

      const cartType = window.themeVariables?.settings?.cartType;
      if (cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
        window.location.href = `${rootRoute()}cart`;
        return;
      }

      // 3. fresh cart payload + theme events; the drawer opens itself on variant:add
      const cartContent = await (await fetch(`${rootRoute()}cart.js`)).json();
      cartContent.sections = responseJson.sections;
      this.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: responseJson.items ?? [responseJson], cart: cartContent }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true, detail: { baseEvent: 'variant:add', cart: cartContent }
      }));
      // the setup has moved into the cart: clear the draft list so markers flip
      // from ring (setup) to check badge (cart), and a re-click can't duplicate
      this.clear();
      this._togglePanel(false);
      this._toast(this.dataset.addedText || 'Setup added to cart');
    } catch (err) {
      console.error('[bt-builder] cart error', err);
      if (errEl) { errEl.textContent = 'Network error — please try again.'; errEl.setAttribute('data-show', ''); }
    } finally {
      if (cta) cta.removeAttribute('aria-busy');
    }
  }

  /* ---------------- copy list ---------------- */
  async copyList() {
    if (!this.state.size) return;
    const origin = window.location.origin;
    let total = 0;
    const lines = [...this.state.entries()].map(([vid, qty]) => {
      const m = registry.get(vid); if (!m) return '';
      total += m.p * qty;
      return `${qty}× ${m.t}${m.o ? ' (' + m.o + ')' : ''} — ${formatMoney(m.p * qty)} · ${origin}${m.url}`;
    }).filter(Boolean);
    const permalink = origin + rootRoute() + 'cart/' + [...this.state.entries()].map(([vid, q]) => `${vid}:${q}`).join(',');
    const txt = `${this.dataset.copyHeading || 'MY BUENOFE SETUP'}\n${lines.join('\n')}\nSubtotal: ${formatMoney(total)}\nAdd it all to cart: ${permalink}`;
    try {
      await navigator.clipboard.writeText(txt);
      this._toast(this.dataset.copiedText || 'Setup list copied');
    } catch (e) {
      this._toast('Copy blocked by browser');
    }
  }

  /* ---------------- toast ---------------- */
  _toast(text) {
    let toast = document.querySelector('.btb-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'btb-toast';
      // aria-live (not role="status"): equivalent a11y announcement, but avoids
      // the store's custom-CSS rule targeting div[role="status"] (grid layout)
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML = '<span class="btb-toast__dot"></span><span class="btb-toast__t"></span>';
      document.body.appendChild(toast);
    }
    toast.querySelector('.btb-toast__t').textContent = text;
    toast.setAttribute('data-show', '');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => toast.removeAttribute('data-show'), 2100);
  }
}

if (!window.customElements.get('bt-builder-app')) {
  window.customElements.define('bt-builder-app', BtBuilderApp);
}
